import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { IconHexagonNumber1Filled, IconHexagonNumber2Filled, IconHexagonNumber3Filled, IconHexagonNumber4Filled, IconHexagonNumber5Filled, IconHexagonNumber6Filled, IconUserCircle, IconClockHour9, IconCircleCheck } from '@tabler/icons-react';
import { projects, navigableProjects } from '../data/projects';
import './ProjectPage.css';
import { usePassword } from '../contexts/PasswordContext';
import PasswordPrompt from '../components/PasswordPrompt';

const ProjectPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const project = projects.find(p => p.id === id);
  const { isAuthenticated } = usePassword();
  
  // Find current project index and get previous/next projects from navigable projects with circular navigation
  const currentIndex = navigableProjects.findIndex(p => p.id === id);
  const previousProject = currentIndex !== -1 ? 
    (currentIndex > 0 ? navigableProjects[currentIndex - 1] : navigableProjects[navigableProjects.length - 1]) : 
    null;
  const nextProject = currentIndex !== -1 ? 
    (currentIndex < navigableProjects.length - 1 ? navigableProjects[currentIndex + 1] : navigableProjects[0]) : 
    null;

  // Set document title based on project
  useEffect(() => {
    if (project) {
      document.title = `${project.title} | Zi Wang's portfolio`;
    } else {
      document.title = 'Project Not Found | Zi Wang\'s portfolio';
    }
  }, [project]);

  // Scroll to top when project changes
  useEffect(() => {
    // Use setTimeout to ensure DOM is fully rendered before scrolling
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      });
    }, 0);
  }, [id]);

  if (!isAuthenticated) {
    return <PasswordPrompt />;
  }

  if (!project) {
    return (
      <div className="project-not-found">
        <div className="container">
          <h1>Project Not Found</h1>
          <p>The project you're looking for doesn't exist.</p>
          <Link to="/" className="back-link">← Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="project-page" data-project-id={project.id}>
      <div className="container">
        <div className="project-header">
          <h1 className="project-title">{project.title}</h1>
          <p className="project-subtitle">{project.description}</p>
          {project.caseStudy && project.caseStudy.sections.length >= 4 && (
            <div className="project-meta-row">
              <div className="project-meta-item">
                <h2><IconClockHour9 size={20} /> {project.caseStudy.sections[2].content}</h2>
              </div>
              <div className="project-meta-item">
                <h2><IconCircleCheck size={20} /> {project.caseStudy.sections[3].content}</h2>
              </div>
              <div className="project-role">
                <h2><IconUserCircle size={20} /> {project.caseStudy.sections[1].content}</h2>
              </div>
            </div>
          )}
        </div>

        <div className="project-hero">
          <img src={project.image} alt={project.title} className="project-hero-image" />
        </div>
      </div>

      {project.caseStudy && (
        <>
          {/* First section: The problem (full width) */}
          {project.caseStudy.sections.length >= 1 && (
            <section className="case-study-section-wrapper background-section">
              <div className="container">
                <div className="case-study-section">
                  <h2>{project.caseStudy.sections[0].title}</h2>
                  {project.caseStudy.sections[0].title === "The problem" ? (
                    <blockquote>{project.caseStudy.sections[0].content}</blockquote>
                  ) : (
                    <p>{project.caseStudy.sections[0].content}</p>
                  )}
                </div>
              </div>
            </section>
          )}

          
          {/* Rest of the sections */}
          {project.caseStudy.sections.slice(4).map((section, index) => (
            <section key={index + 5} className={`case-study-section-wrapper ${index % 2 === 0 ? 'surface-section' : 'background-section'}`}>
              <div className="container">
                <div className="case-study-section">
                  <h2>{section.title}</h2>
                  {section.content && (
                    section.title === "The problem" ? (
                      <blockquote>{section.content}</blockquote>
                    ) : (
                      <p dangerouslySetInnerHTML={{ 
                        __html: section.content
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: var(--color-accent);">$1</a>')
                      }}></p>
                    )
                  )}
                  {section.keyOutcomes && (
                    <div className={['instrumental', 'resourcex', 'growlink'].includes(project.id) ? "key-outcomes-stack" : "key-outcomes-grid"}>
                      {section.keyOutcomes.map((outcome, outcomeIndex) => {
                        const IconComponent = outcomeIndex === 0 ? IconHexagonNumber1Filled : 
                                            outcomeIndex === 1 ? IconHexagonNumber2Filled : 
                                            IconHexagonNumber3Filled;
                        if (['instrumental', 'resourcex', 'growlink'].includes(project.id)) {
                          return (
                            <div key={outcomeIndex} className="vertical-stack-item">
                              <div className="vertical-stack-icon">
                                <IconComponent size={24} />
                              </div>
                              <div className="vertical-stack-content">
                                <p dangerouslySetInnerHTML={{ 
                                  __html: outcome
                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: var(--color-accent);">$1</a>')
                                }}></p>
                              </div>
                            </div>
                          );
                        } else {
                          return (
                            <div key={outcomeIndex} className="key-outcome-card">
                              <div className="key-outcome-header">
                                <IconComponent size={24} className="key-outcome-icon" />
                              </div>
                              <p dangerouslySetInnerHTML={{ 
                                __html: outcome
                                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                  .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: var(--color-accent);">$1</a>')
                              }}></p>
                            </div>
                          );
                        }
                      })}
                    </div>
                  )}
                  {section.solutionItems && (
                    <div className="solution-grid">
                      {section.solutionItems.map((item, itemIndex) => (
                        <div key={itemIndex} className="solution-step">
                          <div className="solution-step-content">
                            <h3>{item}</h3>
                          </div>
                          <div className="solution-step-image">
                            {(() => {
                              const imageData = section.solutionImages?.[itemIndex] || section.solutionImage || "/img/placeholder.png";
                              if (Array.isArray(imageData)) {
                                return (
                                  <div className="solution-image-grid">
                                    {imageData.map((imgSrc, imgIndex) => (
                                      <img 
                                        key={imgIndex}
                                        src={imgSrc} 
                                        alt={`${item} visualization ${imgIndex + 1}`} 
                                      />
                                    ))}
                                  </div>
                                );
                              } else {
                                return (
                                  <img 
                                    src={imageData} 
                                    alt={`${item} visualization`} 
                                  />
                                );
                              }
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>
          ))}

          {/* Research & Discovery - Standalone section with different background */}
          {project.caseStudy.researchDiscovery && (
            <section className="case-study-section-wrapper surface-section">
              <div className="container">
                <div className="case-study-section">
                  <h2>Research & Discovery</h2>
                  <p>{project.caseStudy.researchDiscovery}</p>
                  {project.id === 'deep-visual-correlation' && (
                    <div className="solution-grid">
                      <div className="solution-step">
                        <div className="solution-step-image">
                          <img 
                            src="/img/heuristic assessment.png" 
                            alt="Heuristic assessment visualization" 
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {project.caseStudy.researchTakeaways && (
                    <div className="research-takeaways-grid">
                      {project.caseStudy.researchTakeaways.map((takeaway, takeawayIndex) => {
                        const IconComponent = takeawayIndex === 0 ? IconHexagonNumber1Filled : 
                                            takeawayIndex === 1 ? IconHexagonNumber2Filled : 
                                            takeawayIndex === 2 ? IconHexagonNumber3Filled :
                                            takeawayIndex === 3 ? IconHexagonNumber4Filled :
                                            takeawayIndex === 4 ? IconHexagonNumber5Filled :
                                            IconHexagonNumber6Filled;
                        return (
                          <div key={takeawayIndex} className="key-outcome-card">
                            <div className="key-outcome-header">
                              <IconComponent size={24} className="key-outcome-icon" />
                            </div>
                            <p dangerouslySetInnerHTML={{ 
                              __html: takeaway
                                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: var(--color-accent);">$1</a>')
                            }}></p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {project.caseStudy.userJourneyMapping && (
                    <>
                      <p style={{marginTop: 'calc(var(--space-8) + var(--space-2))'}}>{project.caseStudy.userJourneyMapping}</p>
                      {project.id === 'deep-visual-correlation' && (
                        <div className="solution-grid">
                          <div className="solution-step">
                            <div className="solution-step-image">
                              <img 
                                src="/img/current user journey.png" 
                                alt="Current user journey mapping" 
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {project.caseStudy.platformUsageAnalysis && (
                    <>
                      <p style={{marginTop: 'calc(var(--space-8) + var(--space-2))'}}>{project.caseStudy.platformUsageAnalysis}</p>
                      {project.id === 'deep-visual-correlation' && (
                        <div className="solution-grid">
                          <div className="solution-step">
                            <div className="solution-step-image">
                              <img 
                                src="/img/platform filter usage.png" 
                                alt="Platform usage analysis data" 
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Additional sections: Strategic Pivot, Design Decisions, Validation & Iteration, Lessons Learned */}

          {project.caseStudy.strategicPivot && (
            <section className="case-study-section-wrapper background-section">
              <div className="container">
                <div className="case-study-section">
                  <h2>Strategic pivot</h2>
                  <p>{project.caseStudy.strategicPivot}</p>
                  {project.id === 'deep-visual-correlation' && (
                    <div className="solution-grid">
                      <div className="solution-step">
                        <div className="solution-step-image">
                          <img 
                            src="/img/opportunity map.png" 
                            alt="Opportunity map showing gap between current capabilities and complete optimization loop" 
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {project.caseStudy.designDecisions && (
            <section className="case-study-section-wrapper surface-section">
              <div className="container">
                <div className="case-study-section">
                  <h2>Design decisions</h2>
                  {(() => {
                    const paragraphs = project.caseStudy.designDecisions.split('\n\n');
                    return (
                      <>
                        {/* Design principles description */}
                        <p>{paragraphs[0]}</p>
                        
                        {/* Design principles containers */}
                        {project.caseStudy.designPrinciples && (
                          <div className="design-principles-grid">
                            {project.caseStudy.designPrinciples.map((principle, principleIndex) => {
                              const [title, description] = principle.split(' - ');
                              return (
                                <div key={principleIndex} className="design-principle-card">
                                  <div className="design-principle-title" dangerouslySetInnerHTML={{ 
                                    __html: title.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                  }}></div>
                                  <div className="design-principle-description">- {description}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* User workflow description */}
                        {paragraphs[1] && (
                          <p style={{marginTop: 'calc(var(--space-8) + var(--space-2))'}}>{paragraphs[1]}</p>
                        )}
                        
                        {/* User workflow image */}
                        {project.id === 'deep-visual-correlation' && (
                          <div className="solution-grid">
                            <div className="solution-step">
                              <div className="solution-step-image dark-background">
                                <img 
                                  src="/img/userflow.png" 
                                  alt="User workflow diagram showing investigation touchpoints" 
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </section>
          )}

          {project.caseStudy.validationIteration && (
            <section className="case-study-section-wrapper background-section">
              <div className="container">
                <div className="case-study-section">
                  <h2>Validation & Iteration</h2>
                  <p>{project.caseStudy.validationIteration}</p>
                  {project.id === 'deep-visual-correlation' && (
                    <>
                      <div className="solution-grid">
                        <div className="solution-step">
                          <div className="solution-step-image">
                            <img 
                              src="/img/beta-feedback-1.png" 
                              alt="Beta testing feedback showing correlation setup issues" 
                            />
                          </div>
                        </div>
                      </div>
                      <div className="solution-grid">
                        <div className="solution-step">
                          <div className="solution-step-image">
                            <img 
                              src="/img/beta-feedback-2.png" 
                              alt="Improved 3-step correlation process" 
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </section>
          )}

          {project.caseStudy.lessonsLearned && (
            <section className="case-study-section-wrapper surface-section">
              <div className="container">
                <div className="case-study-section">
                  <h2>Lessons learned</h2>
                  <p>{project.caseStudy.lessonsLearned}</p>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {(project.githubUrl || project.liveUrl) && (
        <section className="case-study-section-wrapper background-section">
          <div className="container">
            <div className="project-links">
              <h2>Links</h2>
              <div className="link-buttons">
                {project.githubUrl && (
                  <a 
                    href={project.githubUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="project-link github"
                  >
                    🐙 View on GitHub
                  </a>
                )}
                {project.liveUrl && (
                  <a 
                    href={project.liveUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="project-link live"
                  >
                    🌐 Live Demo
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact message - hidden for deep visual correlation project */}
      {project.id !== 'deep-visual-correlation' && (
        <section className="case-study-section-wrapper background-section">
          <div className="container">
            <div className="case-study-section">
              <p style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>
                Please <a href="/#contact" style={{ color: 'var(--color-accent)', textDecoration: 'underline' }}>contact me</a> for additional project details.
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="project-navigation surface-section">
        <div className="container">
          <div className="navigation-wrapper">
            <div className="nav-item">
              {previousProject && (
                <Link to={`/project/${previousProject.id}`} className="nav-link prev">
                  <span className="nav-direction">← Previous</span>
                  <span className="nav-title">{previousProject.title}</span>
                </Link>
              )}
            </div>
            
            <div className="nav-item center">
              <Link to="/" className="back-home-button">Back to home</Link>
            </div>
            
            <div className="nav-item">
              {nextProject && (
                <Link to={`/project/${nextProject.id}`} className="nav-link next">
                  <span className="nav-direction">Next →</span>
                  <span className="nav-title">{nextProject.title}</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ProjectPage; 